import React, { useContext, useEffect, useReducer, useState } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { devicesTabReducer } from './devicesTabReducer';
import { DevicesTabContext, devicesTabState, TDevicesTabCountOfItems, TDevicesTabSortBy } from './devicesTabContext';
import { DevicesTabActionTypes } from './devicesTabActions';
import {
	IRestaurantFullInfoContext,
	RestaurantFullInfoContext,
} from 'modules/CustomersAndRestaurants/Restaurants/RestaurantFullInfo/restaurantFullInfoState/restaurantFullInfoContext';
import { IGetPaginatedRestaurantDevices, IRestaurantDevices } from 'types';
import {
	IRestaurantsContext,
	RestaurantsContext,
} from 'modules/CustomersAndRestaurants/Restaurants/Restaurants/restaurantsState/restaurantsContext';
import { getPaginatedRestaurantDevices } from 'backend/restaurants';
import { AxiosResponse } from 'axios';

const DevicesTabState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(devicesTabReducer, devicesTabState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const { restaurantForFullInfo, restaurantFullInfoVisibleForm } =
		useContext<IRestaurantFullInfoContext>(RestaurantFullInfoContext);
	const { fullInfoData } = useContext<IRestaurantsContext>(RestaurantsContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	const needRestaurantDevicesRequest = restaurantForFullInfo && restaurantFullInfoVisibleForm === 'devicesForm';

	const { refetch, response, loading } = useFetchData<
		AxiosResponse<IRestaurantDevices>,
		IGetPaginatedRestaurantDevices
	>(needRestaurantDevicesRequest ? getPaginatedRestaurantDevices : null, {
		restaurantID: fullInfoData.id,
		pageNumber: state.devicesTabCurrentPage,
		pageSize: Number(state.devicesTabCountOfItems),
		searchByName: state.devicesTabSearchString ? state.devicesTabSearchString : null,
		showFreeDevices: false,
		sortByHardwareId:
			state.devicesTabSortBy === 'HardwareIdA-Z' || state.devicesTabSortBy === 'HardwareIdZ-A'
				? state.devicesTabSortBy
				: null,
		sortByTerminalId:
			state.devicesTabSortBy === 'TerminalId1-10' || state.devicesTabSortBy === 'TerminalId10-1'
				? state.devicesTabSortBy
				: null,
	});

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: DevicesTabActionTypes.SET_RESTAURANT_DEVICES,
				payload: {
					restaurantDevices: response.data,
				},
			});
		}
	}, [response]);

	const changeDevicesTabSearchString = (value: string) => {
		dispatch({
			type: DevicesTabActionTypes.CHANGE_DEVICES_TAB_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const setDevicesTabSortBy = (sortBy: TDevicesTabSortBy) => {
		dispatch({
			type: DevicesTabActionTypes.SET_DEVICES_TAB_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setDevicesTabCountOfItems = (count: TDevicesTabCountOfItems) => {
		dispatch({
			type: DevicesTabActionTypes.SET_DEVICES_TAB_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeDevicesTabCurrentPage = (page: number) => {
		dispatch({
			type: DevicesTabActionTypes.CHANGE_DEVICES_TAB_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	useEffect(() => {
		if (isFirstRender) {
			setIsFirstRender(false);
			return;
		}
		refetch();
	}, [
		state.devicesTabCurrentPage,
		state.devicesTabCountOfItems,
		state.devicesTabSearchString,
		state.devicesTabSortBy,
	]);

	return (
		<DevicesTabContext.Provider
			value={{
				changeDevicesTabSearchString,
				setDevicesTabSortBy,
				setDevicesTabCountOfItems,
				changeDevicesTabCurrentPage,
				restaurantDevices: state.restaurantDevices,
				devicesTabSearchString: state.devicesTabSearchString,
				devicesTabSortBy: state.devicesTabSortBy,
				devicesTabCountOfItems: state.devicesTabCountOfItems,
				devicesTabCurrentPage: state.devicesTabCurrentPage,
			}}>
			{children}
		</DevicesTabContext.Provider>
	);
};

export default DevicesTabState;
